import { useMemo } from 'react'
import { History, RefreshCw } from 'lucide-react'
import { Card } from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import { cn } from '@/lib/cn'
import { useDashboardStats } from '../hooks/useDashboardStats'
import { ActionQueueCard } from './sections/ActionQueueCard'
import { AttendanceOverviewCard, AttendanceOverviewModal } from './sections/AttendanceOverviewCard'
import { AttendanceTrendsCard, AttendanceTrendsModal } from './sections/AttendanceTrendsCard'
import { EmployeeAttendanceCard, EmployeeAttendanceModal } from './sections/EmployeeAttendanceCard'
import { KpiDetailModal, KpiStrip } from './sections/KpiStrip'
import { NeedsAttentionCard, NeedsAttentionModal } from './sections/NeedsAttentionCard'
import { RecentClockInsCard, RecentClockInsModal } from './sections/RecentClockInsCard'
import {
  AttendanceMixCard,
  AttendanceMixModal,
  CompanyCoverageCard,
  CompanyCoverageModal,
  PeopleToWatchCard,
  PeopleToWatchModal,
} from './sections/WorkforceCards'
import { DashboardDatePicker } from './sections/DashboardDatePicker'
import { useDashboardDate } from './sections/useDashboardDate'
import { formatUpdated } from './sections/dashboardFormat'
import { useDashboardView } from './sections/useDashboardView'

/**
 * The organization dashboard: headline numbers, then the week (overview,
 * trend, clock-ins, today's roster), then triage and workforce detail. Every
 * card expands into a detail modal addressed by `?view=`.
 */
export function OrganizationDashboardOverview({
  greeting,
  subtitle,
}: {
  /** Page heading. Omitted when the host page renders its own header. */
  greeting?: string
  subtitle?: string
}): React.ReactElement {
  const { date, setDate } = useDashboardDate()
  const { data, isLoading, error, refetch, isFetching } = useDashboardStats({ date })
  const isLive = data?.isLive ?? true
  const asOfLabel = data?.asOf
    ? new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      }).format(new Date(`${data.asOf}T00:00:00Z`))
    : ''
  const { can } = usePermissions()
  const { view, openView, closeView } = useDashboardView()

  const companyNameById = useMemo(
    () => new Map((data?.companyBreakdown ?? []).map((c) => [c.companyId, c.name])),
    [data?.companyBreakdown]
  )
  const companyName = (id: string): string => companyNameById.get(id) ?? 'Unknown company'

  const queueLeaves =
    can('leave', 'approve') || can('leave', 'reject') ? (data?.actionQueue.leaves ?? []) : []
  const queueCorrections =
    can('time_corrections', 'approve') || can('time_corrections', 'reject')
    ? (data?.actionQueue.corrections ?? [])
    : []
  const hasQueue = queueLeaves.length + queueCorrections.length > 0

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 pt-1">
      {greeting ? (
        <div className="min-w-0">
          <h1 className="font-display text-xl font-semibold tracking-[-0.02em] text-gray-900 dark:text-gray-100 sm:text-2xl">
            {greeting}
          </h1>
          {isLive ? (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
          ) : (
            <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <History className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" aria-hidden />
              Looking back at{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">{asOfLabel}</span> ·
              figures as of end of day
            </p>
          )}
        </div>
      ) : (
        <span />
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <DashboardDatePicker date={date} onChange={setDate} />
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          title={
            data?.generatedAt
              ? `Updated ${formatUpdated(data.generatedAt, data.timezone)}${data.timezone ? ` · ${data.timezone}` : ''}`
              : undefined
          }
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} aria-hidden />
          {data?.generatedAt
            ? `Updated ${formatUpdated(data.generatedAt, data.timezone)}`
            : 'Refresh'}
        </button>
      </div>
    </div>
  )

  if (error) {
    return (
      <div className="space-y-6">
        {header}
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Organization dashboard unavailable
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Company navigation is still available below.
              </p>
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
            >
              Retry
            </button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {header}

      <KpiStrip data={data} isLoading={isLoading} onOpen={openView} />

      {hasQueue ? (
        <ActionQueueCard
          leaves={queueLeaves}
          corrections={queueCorrections}
          companyName={companyName}
          timezone={data?.timezone}
        />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-12">
        <AttendanceOverviewCard
          data={data}
          isLoading={isLoading}
          onExpand={() => openView('overview')}
          className="xl:col-span-4"
        />
        <AttendanceTrendsCard
          data={data}
          isLoading={isLoading}
          onExpand={() => openView('trends')}
          className="xl:col-span-5"
        />
        <RecentClockInsCard
          data={data}
          isLoading={isLoading}
          onExpand={() => openView('clock-ins')}
          className="lg:col-span-2 xl:col-span-3 xl:row-span-2"
        />
        <EmployeeAttendanceCard
          data={data}
          isLoading={isLoading}
          onExpand={() => openView('employees')}
          className="lg:col-span-2 xl:col-span-9"
        />
      </div>

      <NeedsAttentionCard
        data={data}
        isLoading={isLoading}
        onExpand={() => openView('attention')}
      />

      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <AttendanceMixCard
          summary={data?.summary}
          isLoading={isLoading}
          onExpand={() => openView('mix')}
        />
        <CompanyCoverageCard
          companies={data?.companyBreakdown ?? []}
          isLoading={isLoading}
          onExpand={() => openView('coverage')}
        />
        <PeopleToWatchCard
          data={data}
          isLoading={isLoading}
          companyName={companyName}
          onExpand={() => openView('watch')}
        />
      </div>

      <KpiDetailModal view={view} data={data} onClose={closeView} />
      <AttendanceOverviewModal open={view === 'overview'} data={data} onClose={closeView} />
      <AttendanceTrendsModal open={view === 'trends'} data={data} onClose={closeView} />
      <RecentClockInsModal open={view === 'clock-ins'} data={data} onClose={closeView} />
      <EmployeeAttendanceModal open={view === 'employees'} data={data} onClose={closeView} />
      <NeedsAttentionModal
        open={view === 'attention'}
        data={data}
        isLoading={isLoading}
        onClose={closeView}
      />
      <AttendanceMixModal open={view === 'mix'} data={data} onClose={closeView} />
      <CompanyCoverageModal open={view === 'coverage'} data={data} onClose={closeView} />
      <PeopleToWatchModal
        open={view === 'watch'}
        data={data}
        companyName={companyName}
        onClose={closeView}
      />
    </div>
  )
}
